import React, { useState, useEffect } from 'react';
import {
  Box,
  TableRow,
  TableCell,
  Collapse,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Theaters as CMI5Icon,
} from '@mui/icons-material';
import { moodleApi } from '../services/moodle-api';
import { environmentApi, versionApi, VersionMetadata } from '../services/api';

interface CMI5Activity {
  id: number;
  name: string;
  cmi5activityid: string;
  courseinfo?: any;
  course?: number;
  tenantId?: string;
  cmi5CourseId?: string;
  key?: string;
  versionInfo?: {
    currentVersion: string;
    totalVersions: number;
    hasVersionHistory: boolean;
  };
}

interface CMI5ActivitiesRowProps {
  sourceId: string;  // Moodle source ID or S3 environment name
  sourceType: 'moodle' | 's3';
  courseId: number;
  courseName: string;
  colSpan: number;
  courseKey?: string;  // S3 file key for the course (needed for version selection tracking)
  selectedActivityVersions?: Record<string, string>;  // Map of activity key to selected version
  onActivityVersionChange?: (courseKey: string, activityKey: string, version: string | null) => void;
}

export const CMI5ActivitiesRow: React.FC<CMI5ActivitiesRowProps> = ({
  sourceId,
  sourceType,
  courseId,
  courseName,
  colSpan,
  courseKey,
  selectedActivityVersions = {},
  onActivityVersionChange,
}) => {
  console.log('[CMI5ActivitiesRow] Rendering:', { sourceId, sourceType, courseId, courseName });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<CMI5Activity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasChecked, setHasChecked] = useState(false);

  // Version selection dialog state
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versionDialogActivity, setVersionDialogActivity] = useState<CMI5Activity | null>(null);
  const [versionDialogVersions, setVersionDialogVersions] = useState<VersionMetadata[]>([]);
  const [versionDialogLoading, setVersionDialogLoading] = useState(false);

  useEffect(() => {
    if (open && !hasChecked) {
      loadActivities();
    }
  }, [open]);

  const loadActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      let result: CMI5Activity[];
      if (sourceType === 'moodle') {
        result = await moodleApi.listCMI5ActivitiesForCourse(sourceId, courseId);
      } else {
        result = await environmentApi.getCMI5ActivitiesForCourse(sourceId, courseId);
      }
      setActivities(result);
      setHasChecked(true);
    } catch (err) {
      console.error('Failed to load CMI5 activities:', err);
      setError(err instanceof Error ? err.message : 'Failed to load CMI5 activities');
    } finally {
      setLoading(false);
    }
  };

  // Open version selection dialog for an activity
  const handleOpenVersionDialog = async (activity: CMI5Activity, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!activity.key) {
      console.warn('Activity has no key, cannot load versions');
      return;
    }

    setVersionDialogActivity(activity);
    setVersionDialogOpen(true);
    setVersionDialogLoading(true);

    try {
      // For S3 activities, extract the filename from the key
      // Key format: {env}/content/cmi5-activities/current/{filename}
      const filename = activity.key.split('/').pop() || activity.key;
      const versions = await versionApi.getVersionHistory('cmi5_activities', filename);
      setVersionDialogVersions(versions);
    } catch (err) {
      console.error('Failed to load version history:', err);
      setVersionDialogVersions([]);
    } finally {
      setVersionDialogLoading(false);
    }
  };

  // Handle version selection
  const handleSelectVersion = (version: string) => {
    if (versionDialogActivity && versionDialogActivity.key && courseKey && onActivityVersionChange) {
      onActivityVersionChange(courseKey, versionDialogActivity.key, version);
    }
    setVersionDialogOpen(false);
  };

  // Clear version selection for an activity
  const handleClearVersion = (activityKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (courseKey && onActivityVersionChange) {
      onActivityVersionChange(courseKey, activityKey, null);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: open ? 0 : undefined }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
            <IconButton
              size="small"
              onClick={() => setOpen(!open)}
              sx={{ p: 0.5 }}
            >
              {open ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
            </IconButton>
            <CMI5Icon fontSize="small" sx={{ color: 'text.secondary', fontSize: '1rem' }} />
            <Typography variant="caption" color="text.secondary">
              CMI5 Activities
            </Typography>
            {!hasChecked && !loading && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                (Click to load)
              </Typography>
            )}
            {loading && <CircularProgress size={12} />}
            {hasChecked && !loading && activities.length > 0 && (
              <Chip
                label={`${activities.length}`}
                size="small"
                sx={{ height: 16, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ py: 0, px: 0, borderBottom: open ? undefined : 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1, px: 2, pl: 8, bgcolor: 'action.hover' }}>
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Loading CMI5 activities...
                  </Typography>
                </Box>
              )}

              {error && (
                <Typography variant="caption" color="error" sx={{ py: 1, display: 'block' }}>
                  {error}
                </Typography>
              )}

              {!loading && !error && activities.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ py: 1, display: 'block', fontStyle: 'italic' }}>
                  No CMI5 activities found for this course
                </Typography>
              )}

              {!loading && !error && activities.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {activities.map((activity) => {
                    // Handle both Moodle (courseinfo) and S3 (direct properties) sources
                    const tenantId = activity.tenantId || activity.courseinfo?.tenantId || 'N/A';
                    const cmi5CourseId = activity.cmi5CourseId || activity.courseinfo?.id || activity.id;

                    return (
                      <Box
                        key={activity.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                          px: 1,
                          bgcolor: 'background.paper',
                          borderRadius: 0.5,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <CMI5Icon fontSize="small" sx={{ color: 'primary.main', fontSize: '0.9rem' }} />
                        <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem' }}>
                          {activity.name}
                        </Typography>
                        <Chip
                          label={`Tenant: ${tenantId}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                        <Chip
                          label={`Course: ${cmi5CourseId}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                        {/* Version badge and selection for S3 activities */}
                        {sourceType === 's3' && activity.versionInfo && activity.versionInfo.hasVersionHistory && activity.key && (
                          <>
                            <Chip
                              label={`${activity.versionInfo.currentVersion} (${activity.versionInfo.totalVersions})`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                            {selectedActivityVersions[activity.key] ? (
                              <Chip
                                label={`Use ${selectedActivityVersions[activity.key]}`}
                                size="small"
                                color="success"
                                variant="filled"
                                sx={{ height: 16, fontSize: '0.6rem', cursor: 'pointer' }}
                                onDelete={(e) => handleClearVersion(activity.key!, e)}
                                onClick={(e) => handleOpenVersionDialog(activity, e)}
                              />
                            ) : (
                              <Chip
                                label="Select Version"
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.6rem', cursor: 'pointer' }}
                                onClick={(e) => handleOpenVersionDialog(activity, e)}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Version Selection Dialog */}
      <Dialog
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Activity Version
          {versionDialogActivity && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {versionDialogActivity.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {versionDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : versionDialogVersions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No version history available
            </Typography>
          ) : (
            <List>
              {versionDialogVersions.map((version) => (
                <ListItem key={version.version} disablePadding>
                  <ListItemButton onClick={() => handleSelectVersion(version.version)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{version.version}</Typography>
                          {version.version === versionDialogActivity?.versionInfo?.currentVersion && (
                            <Chip label="Current" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" component="div">
                            Uploaded by: {version.uploadedBy || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" component="div">
                            {new Date(version.timestamp).toLocaleString()}
                          </Typography>
                          {version.description && (
                            <Typography variant="caption" component="div">
                              {version.description}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
